





















































import { plainToInstance } from 'class-transformer'
import { cloneDeep } from 'lodash'
import { Component, Emit, Prop, VModel, Watch } from 'vue-property-decorator'

import { successNotificationWrapper } from '../../../logic/helpers/NotificationService'
import { IMessageSettings } from '../NewMessageEditorWithMedia/Body/logic/types'
import MessageEditorWithMedia from '../NewMessageEditorWithMedia/MessageEditorWithMedia.vue'
import MessageEditorWithMediaData, { IMessageTemplate } from '../NewMessageEditorWithMedia/types'
import MessageEditorPropsMixin from '../PropsMixin'
import MessagesNavigationButtons from './MessagesNavigationButtons.vue'
import { PiningPartsHelper, PinMessageParts, PinPartActions } from './types'

@Component({
  components: {
    MessageEditorWithMedia,
    MessagesNavigationButtons,
  }
})
export default class MultiMessageEditorWithMedia extends MessageEditorPropsMixin {
  @VModel() messages!: Array<MessageEditorWithMediaData>

  @Prop({ type: Boolean, default: false }) readonly overwriteButton !: boolean

  @Prop({ default: PinMessageParts.None }) pinMedia!: PinMessageParts

  @Prop({ default: PinMessageParts.None }) pinButtons!: PinMessageParts

  @Prop() defaultValue!: Array<IMessageTemplate>

  @Watch('messages')
  onMessagesChange(data: Array<MessageEditorWithMediaData>) {
    if (this.currentMessageIndex > data.length) {
      this.currentMessageIndex = data.length
    }

    if (!data.length) {
      return this.emptyEditor()
    }
  }

  attachmentsIsLoading = false

  rerenderKey = 0

  @Emit()
  emptyEditor() {
    return null
  }

  get piningMediaHelper(): PiningPartsHelper | undefined {
    if (this.pinMedia === PinMessageParts.PinEditable) {
      return {
        type: PinMessageParts.PinEditable,
        callback: this.pinMediaAction,
        piningState: this.pinMediaState
      }
    }

    if (this.pinMedia === PinMessageParts.PinNoEditable) {
      return { type: PinMessageParts.PinNoEditable }
    }

    return undefined
  }

  pinMediaState = false

  pinMediaAction(arg: PinPartActions): void {
    if (arg === PinPartActions.CopyPart) {
      this.messages.forEach((m) => m.attachments = this.model.attachments)
      successNotificationWrapper(this.$t('success_alert').toString(), this.$t('message_editor_media_cupy_action_success_text').toString())
    }

    if (arg === PinPartActions.UnpinPart) {
      this.pinMediaState = false
      successNotificationWrapper(this.$t('success_alert').toString(), this.$t('message_editor_media_unpin_action_success_text').toString())
    }

    if (arg === PinPartActions.PinPart) {
      this.pinMediaState = true
      this.messages.forEach((m) => m.attachments = this.model.attachments)
      successNotificationWrapper(this.$t('success_alert').toString(), this.$t('message_editor_media_pin_action_success_text').toString())
    }
  }

  get piningButtonsHelper(): PiningPartsHelper | undefined {
    if (this.pinButtons === PinMessageParts.PinEditable) {
      return {
        type: PinMessageParts.PinEditable,
        callback: this.pinButtonsAction,
        piningState: this.pinButtonsState
      }
    }

    if (this.pinMedia === PinMessageParts.PinNoEditable) {
      return { type: PinMessageParts.PinNoEditable }
    }

    return undefined
  }

  pinButtonsState = false

  pinButtonsAction(arg: PinPartActions): void {
    if (arg === PinPartActions.CopyPart) {
      this.messages.forEach((m) => m.buttons = this.model.buttons)
      successNotificationWrapper(this.$t('success_alert').toString(), this.$t('message_editor_buttons_cupy_action_success_text').toString())
    }

    if (arg === PinPartActions.UnpinPart) {
      this.pinButtonsState = false
      successNotificationWrapper(this.$t('success_alert').toString(), this.$t('message_editor_buttons_unpin_action_success_text').toString())
    }

    if (arg === PinPartActions.PinPart) {
      this.pinButtonsState = true
      this.messages.forEach((m) => m.buttons = this.model.buttons)
      successNotificationWrapper(this.$t('success_alert').toString(), this.$t('message_editor_buttons_pin_action_success_text').toString())
    }
  }

  currentMessageIndex = 0

  @Watch('currentMessageIndex')
  onMessageIndexChange() {
    this.rerenderKey += 1
  }

  editorMessageLimit = 4096

  downgradeCounterMax(): void {
    this.editorMessageLimit = 1024
  }

  resetCounterMax(): void {
    this.editorMessageLimit = 4096
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  addMessage(): void {
    if (this.messages.length < (this.maxMessagesCount ?? 12)) {
      const attachments = this.messages.length ? this.messages[0].attachments : []
      const buttons = this.messages.length ? this.messages[0].buttons : []

      let messages = this.messages
      let settingsStruct: Partial<IMessageSettings> = { ...this.settingsStructure ?? {} }

      if (messages.length) {
        const { text, buttons, attachments, topics, ...settings } = messages[0]

        settingsStruct = Object.assign<Partial<IMessageSettings>, Partial<IMessageSettings>>(settingsStruct, settings)
      }

      if (this.defaultValue && this.defaultValue.length) {
        const templates = this.defaultValue.map(v => ({
          ...v,
          attachments: this.pinMediaState ? attachments : [],
          buttons: this.pinButtons ? buttons : [],
          ...settingsStruct,
          ...this.topics ? { topics: [] } : {}
        }))

        messages = messages.concat(templates)
      } else {
        messages = messages.concat([
          plainToInstance(MessageEditorWithMediaData, {
            text: '',
            pin: false,
            disable_link_preview: false,
            disable_notify: false,
            remove_previous: false,
            protect_content: false,
            remove_after: 0,
            send_after: 0,
            ...settingsStruct,
            attachments: this.pinMediaState ? attachments : [],
            buttons: this.pinButtons ? buttons : [],
            ...this.topics ? { topics: [] } : {}
          })
        ])
      }

      this.messages = messages

      this.$nextTick(() => {
        this.currentMessageIndex = this.messages.length - 1
      })
    }
  }

  removeMessage(): void {
    this.messages = this.messages.filter((m, i) => i !== this.currentMessageIndex)

    if (this.currentMessageIndex !== 0) {
      this.currentMessageIndex -= 1
    }

    this.rerenderKey += 1
  }

  get model(): MessageEditorWithMediaData {
    return this.messages[this.currentMessageIndex]
  }

  set model(model: MessageEditorWithMediaData) {
    const oldMessages = cloneDeep(this.messages)

    oldMessages.splice(this.currentMessageIndex, 1, model)

    if (this.pinMediaState) {
      oldMessages.forEach((m) => m.attachments = model.attachments)
    }

    if (this.pinButtonsState) {
      oldMessages.forEach((m) => m.buttons = model.buttons)
    }

    const { buttons, attachments, text, topics, ...settings } = model

    oldMessages.forEach(m => {
      m.send_after = settings.send_after;
      m.disable_link_preview = settings.disable_link_preview;
      m.disable_notify = settings.disable_notify;
      m.pin = settings.pin;
      m.protect_content = settings.protect_content;
      m.remove_after = settings.remove_after;
      m.remove_previous = settings.remove_previous;
    })

    this.messages = oldMessages
  }

  created(): void {
    if (this.messages.length === 0) {
      this.currentMessageIndex = -1
    }

    if (this.pinMedia && this.pinMedia === PinMessageParts.PinNoEditable) {
      this.pinMediaState = true
    }

    if (this.pinButtons && this.pinButtons === PinMessageParts.PinNoEditable) {
      this.pinButtonsState = true
    }
  }
}
